/**
 * CR500: DealCardSpec v2 Resolver
 * 
 * Merges variant template + deal-type overrides into a final render spec.
 * Never throws - falls back to defaults for malformed/missing data.
 */

import type {
  DealCardSpecV2,
  DealCardOverridesV2,
  ResolvedDealCardSpecV2,
  DealCardBlockSpec,
  DealCardSurface,
  SurfaceOverrides,
} from "./types";
import { isDealCardSpecV2, isDealCardOverridesV2, isValidBlockType } from "./types";
import { filterThemeOwnedFromStyleOverrides } from "./themeOwnedGuard";

// ===============================================================
// DEFAULT FALLBACK SPEC
// ===============================================================

/**
 * The standard_v2 default spec - used when variant is missing or invalid.
 */
export const DEFAULT_SPEC_V2: DealCardSpecV2 = {
  schemaVersion: 2,
  blockCatalogVersion: 1,
  surfaces: {
    primary: {
      blocks: [
        { id: "header", type: "header", enabled: true, order: 0 }, // Phase 2E: now extracted
        { id: "ribbon", type: "ribbon", enabled: true, order: 1 },
        { id: "starburst", type: "starburst", enabled: true, order: 2 }, // Phase 3: now extracted
        { id: "meta_pills", type: "meta_pills", enabled: true, order: 3 }, // Phase 4A: deal type + status pills
        { id: "headline", type: "headline", enabled: true, order: 4 },
        { id: "promo_badges", type: "promo_badges", enabled: true, order: 5 },
        { id: "reward_box", type: "reward_box", enabled: true, order: 6 }, // Phase 2C: now extracted
        { id: "details_pills", type: "details_pills", enabled: true, order: 7 },
        { id: "value_box", type: "value_box", enabled: true, order: 8 }, // Phase 2C: now extracted
        { id: "spend_box", type: "spend_box", enabled: true, order: 9 }, // Phase 2C: now extracted
        { id: "dates", type: "dates", enabled: true, order: 10 },
        { id: "flyer_snippet", type: "flyer_snippet", enabled: true, order: 11 }, // CR413: Flyer evidence thumbnail (self-hides when no data)
        { id: "cta_button", type: "cta_button", enabled: true, order: 12 },
        { id: "details_cta", type: "details_cta", enabled: true, order: 13 }, // CR309
        { id: "feedback", type: "feedback", enabled: true, order: 14 }, // CR508: Deal feedback control
      ],
    },
    details: {
      blocks: [
        { id: "terms", type: "terms", enabled: true, order: 0, config: { maxLines: 3 } },
        { id: "fine_print", type: "fine_print", enabled: true, order: 1, config: { maxLines: 2 } },
        { id: "source", type: "source", enabled: true, order: 2 },
        { id: "evidence", type: "evidence", enabled: true, order: 3 },
        { id: "validator", type: "validator", enabled: false, order: 4 },
        { id: "flyer_snippet_details", type: "flyer_snippet_details", enabled: true, order: 5 }, // CR335
        { id: "actions", type: "actions", enabled: true, order: 6 },
      ],
    },
  },
  defaults: {
    headerVariant: "standard",
    valueBoxVariant: "standard",
    secondaryDetailsMode: "flip",
  },
};

/**
 * Default labels by deal type.
 */
export const DEFAULT_LABELS: Record<string, Record<string, string>> = {
  BONUS: {
    effective_value_title: "Effective Value",
    reward_title: "Bonus",
    qualifying_spend_title: "Spend Required",
    eligibility_title: "Eligibility",
  },
  DISCOUNT: {
    effective_value_title: "Savings",
    reward_title: "Reward",
    qualifying_spend_title: "Qualifying Spend",
    eligibility_title: "Eligibility",
  },
  POINTS: {
    effective_value_title: "Points Value",
    reward_title: "Points",
    qualifying_spend_title: "Spend Required",
    eligibility_title: "Eligibility",
  },
  IN_KIND: {
    effective_value_title: "Value",
    reward_title: "Free Gift",
    qualifying_spend_title: "Qualifying Purchase",
    eligibility_title: "Eligibility",
  },
  UNCLASSIFIED: {
    effective_value_title: "Value",
    reward_title: "Reward",
    qualifying_spend_title: "Qualifying Spend",
    eligibility_title: "Eligibility",
  },
};

// ===============================================================
// RESOLVER FUNCTIONS
// ===============================================================

/**
 * Parse a raw JSON value into a DealCardSpecV2.
 * Returns null if invalid, never throws.
 */
export function parseSpecV2(raw: unknown): DealCardSpecV2 | null {
  try {
    if (isDealCardSpecV2(raw)) {
      return raw;
    }
    // Try parsing if it's a string
    if (typeof raw === "string") {
      const parsed = JSON.parse(raw);
      if (isDealCardSpecV2(parsed)) {
        return parsed;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Parse a raw JSON value into DealCardOverridesV2.
 * Returns null if invalid, never throws.
 */
export function parseOverridesV2(raw: unknown): DealCardOverridesV2 | null {
  try {
    if (isDealCardOverridesV2(raw)) {
      return raw;
    }
    if (typeof raw === "string") {
      const parsed = JSON.parse(raw);
      if (isDealCardOverridesV2(parsed)) {
        return parsed;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Apply block toggles to a surface, respecting enabled state.
 * CR309: Ribbon visibility is EXCLUSIVELY controlled by blockToggles.ribbon.
 * When blockToggles.ribbon is not explicitly true, ribbon is disabled.
 */
function applyBlockToggles(
  surface: DealCardSurface,
  toggles?: Record<string, boolean>
): DealCardSurface {
  if (!toggles || Object.keys(toggles).length === 0) {
    // CR309: Even without toggles, apply SSOT rules for ribbon - default to disabled
    return {
      blocks: surface.blocks.map((block) => {
        // CR309: Ribbon requires explicit blockToggles.ribbon=true to render
        // When no toggles exist, ribbon defaults to DISABLED for safety (no forced ON)
        if (block.id === 'ribbon') {
          return { ...block, enabled: false };
        }
        return block;
      }),
    };
  }
  
  return {
    blocks: surface.blocks.map((block) => {
      const toggle = toggles[block.id];
      // CR309: For ribbon, REQUIRE explicit true to enable. Missing/undefined = disabled for safety.
      // For other blocks, keep original logic: explicit values override, else keep registry default.
      if (block.id === 'ribbon') {
        // Ribbon: Only enabled if toggles.ribbon === true explicitly
        const enabled = toggle === true;
        return { ...block, enabled };
      }
      // Other blocks: explicit boolean overrides, else keep block default
      const enabled = toggle === false ? false : (toggle === true ? true : block.enabled);
      return { ...block, enabled };
    }),
  };
}

/**
 * CR309: Apply custom block order to a surface.
 * Blocks are sorted according to blockOrder array, any blocks not in the order 
 * are appended at the end in their original order.
 */
function applyBlockOrder(
  surface: DealCardSurface,
  blockOrder?: string[]
): DealCardSurface {
  if (!blockOrder || blockOrder.length === 0) {
    return surface;
  }
  
  // Create a map for O(1) lookup of order positions
  const orderMap = new Map<string, number>();
  blockOrder.forEach((id, index) => orderMap.set(id, index));
  
  // Separate blocks into ordered and unordered
  const orderedBlocks: DealCardBlockSpec[] = [];
  const unorderedBlocks: DealCardBlockSpec[] = [];
  
  for (const block of surface.blocks) {
    if (orderMap.has(block.id)) {
      orderedBlocks.push(block);
    } else {
      unorderedBlocks.push(block);
    }
  }
  
  // Sort ordered blocks by their position in blockOrder
  orderedBlocks.sort((a, b) => {
    const orderA = orderMap.get(a.id) ?? Infinity;
    const orderB = orderMap.get(b.id) ?? Infinity;
    return orderA - orderB;
  });
  
  // Combine: ordered blocks first, then unordered blocks (in original order)
  // CR431: Update .order property so sanitizeBlocks preserves custom ordering
  const combined = [...orderedBlocks, ...unorderedBlocks];
  return {
    blocks: combined.map((block, index) => ({ ...block, order: index })),
  };
}

/**
 * Apply config overrides to blocks in a surface.
 */
function applyBlockConfigs(
  surface: DealCardSurface,
  configs?: Record<string, Record<string, unknown>>
): DealCardSurface {
  if (!configs || Object.keys(configs).length === 0) {
    return surface;
  }
  
  return {
    blocks: surface.blocks.map((block) => {
      const configOverride = configs[block.id];
      if (!configOverride) return block;
      return {
        ...block,
        config: { ...block.config, ...configOverride },
      };
    }),
  };
}

/**
 * Apply surface overrides to a surface.
 * CR309: Now includes blockOrder handling.
 */
function applySurfaceOverrides(
  surface: DealCardSurface,
  overrides?: SurfaceOverrides
): DealCardSurface {
  if (!overrides) return surface;
  
  let result = surface;
  result = applyBlockToggles(result, overrides.blockToggles);
  result = applyBlockConfigs(result, overrides.blockConfigs);
  // CR309: Apply custom block order after toggles and configs
  result = applyBlockOrder(result, overrides.blockOrder);
  return result;
}

/**
 * Sort blocks by order and filter out invalid types.
 */
function sanitizeBlocks(blocks: DealCardBlockSpec[]): DealCardBlockSpec[] {
  return blocks
    .filter((block) => isValidBlockType(block.type))
    .sort((a, b) => a.order - b.order);
}

/**
 * Ensure a surface has a specific block ID/type pair.
 * Used for safe rollout when legacy variants don't yet include new universal blocks.
 */
function ensureBlockPresent(
  blocks: DealCardBlockSpec[],
  candidate: DealCardBlockSpec
): DealCardBlockSpec[] {
  const exists = blocks.some((b) => b.id === candidate.id || b.type === candidate.type);
  if (exists) return blocks;

  const nextOrder = blocks.length > 0 ? Math.max(...blocks.map((b) => b.order)) + 1 : candidate.order;
  return [...blocks, { ...candidate, order: nextOrder }];
}

/**
 * CR306: Normalize legacy styling.* into styleOverrides.* for V2 compatibility.
 * CR310: For V2 (schemaVersion=2), ribbon visibility is SOLELY from blockToggles.ribbon
 *        - Do NOT copy showRibbon from legacy styling
 *        - Strip showRibbon from final output
 * 
 * @param overrides - The overrides object (may have legacy styling nested in it)
 * @param legacyStyling - Legacy styling object from template_json.styling
 * @param isV2 - Whether this is a V2 spec (schemaVersion=2)
 */
export function normalizeStyleOverrides(
  overrides: DealCardOverridesV2 | null,
  legacyStyling?: Record<string, unknown> | null,
  isV2: boolean = true
): Record<string, unknown> {
  const existingOverrides = overrides?.styleOverrides || {};
  
  // If V2 styleOverrides already has values, use them (they take precedence)
  // Only copy from legacy if V2 values are missing
  const normalized: Record<string, unknown> = { ...existingOverrides };
  
  if (legacyStyling) {
    // CR306: Copy forward legacy values only if V2 values don't exist
    if (normalized.showMerchantLogo === undefined && legacyStyling.showMerchantLogo !== undefined) {
      normalized.showMerchantLogo = legacyStyling.showMerchantLogo;
    }
    if (normalized.showMerchantName === undefined && legacyStyling.showMerchantName !== undefined) {
      normalized.showMerchantName = legacyStyling.showMerchantName;
    }
    // CR310: For V2, do NOT copy showRibbon from legacy - blockToggles.ribbon is SSoT
    if (!isV2) {
      if (normalized.showRibbon === undefined && legacyStyling.showRibbon !== undefined) {
        normalized.showRibbon = legacyStyling.showRibbon;
      }
    }
    if (normalized.showStarburst === undefined && legacyStyling.showStarburst !== undefined) {
      normalized.showStarburst = legacyStyling.showStarburst;
    }
  }
  
  // CR315: Read-guard - filter out theme-owned keys (Theme Labs always wins)
  // CR310: Also strip showRibbon for V2 (it's now controlled by blockToggles only)
  const filtered = filterThemeOwnedFromStyleOverrides(normalized);
  if (isV2) {
    delete filtered.showRibbon;
  }
  return filtered;
}

/**
 * Resolve a DealCardSpec v2 by merging variant template with deal-type overrides.
 * 
 * @param variantSpec - The base variant template (from DB or fallback)
 * @param overrides - Deal-type specific overrides (from DB or null)
 * @param dealTypeKey - The deal type this resolution is for
 * @param variantKey - The variant key being used
 * @param sources - Source tracking for debugging
 * @param legacyStyling - Optional legacy styling from template_json.styling for migration
 * @param components - CR325: Component configs from template_json.components
 */
export function resolveDealCardSpecV2(
  variantSpec: DealCardSpecV2 | null,
  overrides: DealCardOverridesV2 | null,
  dealTypeKey: string,
  variantKey: string,
  sources: { variant: "db" | "fallback"; overrides: "db" | "fallback" },
  legacyStyling?: Record<string, unknown> | null,
  components?: Record<string, unknown> | null
): ResolvedDealCardSpecV2 {
  // CR327: Increment spec resolve counter for stability debugging
  (async () => {
    try {
      const { incrementPreviewCounter } = await import("@/components/admin/deal-cards/PreviewStabilityCounters");
      incrementPreviewCounter("specResolves");
    } catch {
      // Counter module may not be loaded in production
    }
  })();
  
  // Use default spec if variant is missing or invalid
  const baseSpec = variantSpec ?? DEFAULT_SPEC_V2;
  const usedFallback = !variantSpec;
  
  // Start with base surfaces
  let primarySurface = { ...baseSpec.surfaces.primary };
  let detailsSurface = { ...baseSpec.surfaces.details };
  
  // Apply overrides if present
  if (overrides?.surfaceOverrides) {
    primarySurface = applySurfaceOverrides(
      primarySurface,
      overrides.surfaceOverrides.primary
    );
    detailsSurface = applySurfaceOverrides(
      detailsSurface,
      overrides.surfaceOverrides.details
    );
  }
  
  // Sanitize and sort blocks
  primarySurface = { blocks: sanitizeBlocks(primarySurface.blocks) };
  detailsSurface = { blocks: sanitizeBlocks(detailsSurface.blocks) };

  // CR508: Backfill feedback block for variants saved before the block existed.
  primarySurface = {
    blocks: sanitizeBlocks(
      ensureBlockPresent(primarySurface.blocks, {
        id: "feedback",
        type: "feedback",
        enabled: true,
        order: 14,
      })
    ),
  };
  
  // Merge labels: default -> override
  const defaultLabels = DEFAULT_LABELS[dealTypeKey.toUpperCase()] || DEFAULT_LABELS.UNCLASSIFIED;
  const mergedLabels = {
    ...defaultLabels,
    ...(overrides?.labels || {}),
  };
  
  // CR306: Normalize style overrides with legacy shim
  const mergedStyleOverrides = normalizeStyleOverrides(overrides, legacyStyling);
  
  return {
    schemaVersion: 2,
    blockCatalogVersion: baseSpec.blockCatalogVersion,
    surfaces: {
      primary: primarySurface,
      details: detailsSurface,
    },
    defaults: baseSpec.defaults,
    resolved: {
      variantKey,
      dealTypeKey: dealTypeKey.toUpperCase(),
      labels: mergedLabels,
      styleOverrides: mergedStyleOverrides,
      usedFallback,
      sources,
      // CR325: Pass through component configs for v2 rendering
      components: components ?? undefined,
    },
  };
}

/**
 * Get enabled blocks for a surface (convenience helper).
 */
export function getEnabledBlocks(
  spec: ResolvedDealCardSpecV2,
  surface: "primary" | "details"
): DealCardBlockSpec[] {
  return spec.surfaces[surface].blocks.filter((b) => b.enabled);
}

/**
 * Compute a debug fingerprint for parity checking.
 */
export function computeSpecFingerprint(spec: ResolvedDealCardSpecV2): string {
  const primary = spec.surfaces.primary.blocks
    .filter((b) => b.enabled)
    .map((b) => b.id)
    .join(",");
  const details = spec.surfaces.details.blocks
    .filter((b) => b.enabled)
    .map((b) => b.id)
    .join(",");
  return `v2:${spec.resolved.variantKey}:p[${primary}]:d[${details}]`;
}
